import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, SmilePlus, Trash2 } from "lucide-react";
import { formatPrice, formatRelativeTime } from "./ProductItem.utils";
import type { HawkFindPost } from "../services/ProductsService";

interface HawkFindCardProps {
  post: HawkFindPost;
  retailerLabel: string;
  onCheckNearby: (post: HawkFindPost) => void;
  reactionOptions: readonly string[];
  onReact: (post: HawkFindPost, reaction: string) => void;
  canDelete: boolean;
  isDeleting?: boolean;
  onDelete: (post: HawkFindPost) => void;
}

const retailerAccentClasses: Record<string, string> = {
  walmart: "text-blue-600 dark:text-blue-400",
  walmart_online: "text-blue-600 dark:text-blue-400",
  target: "text-red-600 dark:text-red-400",
  target_online: "text-red-600 dark:text-red-400",
  home_depot: "text-orange-600 dark:text-orange-400",
  bestbuy: "text-blue-800 dark:text-blue-300",
  costco: "text-cyan-700 dark:text-cyan-400",
  dollar_general: "text-amber-600 dark:text-amber-400",
  woot: "text-violet-600 dark:text-violet-400",
};

export default function HawkFindCard({
  post,
  retailerLabel,
  onCheckNearby,
  reactionOptions,
  onReact,
  canDelete,
  isDeleting,
  onDelete,
}: HawkFindCardProps) {
  const [isReactionPickerOpen, setIsReactionPickerOpen] = useState(false);
  const [animatedEmojis, setAnimatedEmojis] = useState<Record<string, boolean>>({});
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const reactionCounters = reactionOptions
    .map((emoji) => ({ emoji, count: post.reactions?.[emoji] ?? 0 }))
    .filter((entry) => entry.count > 0);
  const totalReactionCount = reactionCounters.reduce((sum, e) => sum + e.count, 0);
  const discountPct =
    post.ogPrice > 0
      ? Math.min(99, Math.round(((post.ogPrice - post.discountPrice) / post.ogPrice) * 100))
      : 0;
  const hasSku = !!post.sku?.trim();
  const retailerColorClass = retailerAccentClasses[post.retailer] || "text-gray-600 dark:text-gray-400";
  const postedAtLabel = post.timestamp > 0 ? formatRelativeTime(post.timestamp) : "";

  const runReactionAnimation = useCallback((emojis: string[]) => {
    if (!emojis.length) return;
    setAnimatedEmojis((prev) => {
      const next = { ...prev };
      for (const emoji of emojis) next[emoji] = true;
      return next;
    });
    window.setTimeout(() => {
      setAnimatedEmojis((prev) => {
        const next = { ...prev };
        for (const emoji of emojis) delete next[emoji];
        return next;
      });
    }, 220);
  }, []);

  const handleReactClick = useCallback((emoji: string) => {
    onReact(post, emoji);
    runReactionAnimation([emoji]);
    setIsReactionPickerOpen(false);
  }, [onReact, post, runReactionAnimation]);

  useEffect(() => {
    if (!isReactionPickerOpen) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!pickerRef.current || !target) return;
      if (!pickerRef.current.contains(target)) setIsReactionPickerOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isReactionPickerOpen]);

  return (
    <article className="overflow-hidden border-b border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        <img src="/hawk_icon_white.svg" alt="DealHawk" className="h-9 w-9 rounded-full bg-gray-900 p-1.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs font-semibold ${retailerColorClass}`}>{retailerLabel}</span>
            {!!postedAtLabel && (
              <span className="text-[11px] text-gray-500 dark:text-dark-300">
                {postedAtLabel}
              </span>
            )}
          </div>
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(post)}
            disabled={isDeleting}
            className="p-1.5 text-gray-400 hover:text-red-500 dark:text-dark-400 dark:hover:text-red-400 transition-colors disabled:opacity-50 cursor-pointer shrink-0"
            style={{ touchAction: "manipulation" }}
            title="Delete post"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ── Image ── */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-dark-700">
        <img
          src={post.photo}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* ── Action row ── */}
      <div
        className="bg-white px-4 pb-3 pt-2 dark:bg-dark-800"
        ref={pickerRef}
      >
        <div className="relative flex items-center gap-2">
          <button
            onClick={() => setIsReactionPickerOpen((prev) => !prev)}
            className={`inline-flex h-8 items-center justify-center gap-1.5 px-1.5 text-sm font-medium transition-colors cursor-pointer shrink-0 ${
              isReactionPickerOpen
                ? "text-purple-700 dark:text-purple-300"
                : "text-gray-600 hover:text-gray-900 dark:text-dark-200 dark:hover:text-white"
            }`}
            style={{ touchAction: "manipulation" }}
            title="React to this post"
          >
            <SmilePlus className="h-4 w-4" />
            <span className="text-xs">React</span>
          </button>

          {reactionCounters.length > 0 && (
            <button
              onClick={() => setIsReactionPickerOpen((prev) => !prev)}
              className="flex items-center gap-px px-1 py-0.5 transition-colors text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-dark-100"
              style={{ touchAction: "manipulation" }}
              title="View reactions"
            >
              {reactionCounters.map((entry) => (
                <span
                  key={entry.emoji}
                  className={`w-6 h-6 inline-flex items-center justify-center text-[15px] leading-none rounded-full transition-all duration-200 ease-out ${
                    animatedEmojis[entry.emoji] ? "scale-150 -translate-y-1" : "scale-100 translate-y-0"
                  } ${post.userReaction === entry.emoji ? "z-10 drop-shadow-sm" : ""}`}
                >
                  {entry.emoji}
                </span>
              ))}
              <span className="text-xs font-semibold text-gray-500 dark:text-dark-300 tabular-nums ml-1 mr-0.5">
                {totalReactionCount}
              </span>
            </button>
          )}

          <button
            onClick={() => onCheckNearby(post)}
            disabled={!hasSku}
            className="ml-auto inline-flex h-7 items-center justify-center gap-1 px-1 text-xs font-semibold text-gray-700 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-dark-100 dark:hover:text-white dark:disabled:text-dark-400"
            style={{ touchAction: "manipulation" }}
            title={hasSku ? "Check stock at nearby stores" : "Store lookup unavailable"}
          >
            <MapPin className="h-3.5 w-3.5" />
            Check stock
          </button>

          {isReactionPickerOpen && (
            <div className="absolute bottom-full left-0 z-20 mb-2 flex items-center gap-0.5 border border-gray-200 bg-white px-1.5 py-1 shadow-xl animate-[popIn_150ms_ease-out] dark:border-dark-600 dark:bg-dark-700">
              {reactionOptions.map((emoji, i) => (
                <button
                  key={emoji}
                  onClick={() => handleReactClick(emoji)}
                  className={`h-9 w-9 inline-flex items-center justify-center text-xl hover:scale-[1.25] active:scale-90 transition-all duration-150 cursor-pointer ${
                    post.userReaction === emoji ? "bg-gray-100 dark:bg-dark-600" : ""
                  }`}
                  style={{ touchAction: "manipulation", animationDelay: `${i * 30}ms` }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Title + price ── */}
      <div className="px-4 pb-1 pt-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2 break-words mb-2">
          {post.title}
        </h3>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-bold leading-none text-gray-900 tabular-nums dark:text-white">
            {formatPrice(post.discountPrice)}
          </span>
          <span className="text-xs text-gray-400 line-through tabular-nums">
            {formatPrice(post.ogPrice)}
          </span>
          {discountPct > 0 && (
            <span className="text-[9px] font-semibold text-red-500">
              {discountPct}% OFF
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
